import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  Chip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { pincodeApi } from 'src/api/pincode';
import { Pincodes } from 'src/interfaces/masters/ModelMaster/model';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { getUniqueIds } from 'src/components/masters/customFunctions';
import { InputField } from 'src/components/input-field';
import { DayAreas } from 'src/interfaces/new-channel-partner/channelPartner';

interface AddEWastePincodeProps {
  open: boolean;
  onClose: () => void;
  cpServiceAreaIds: string[];
  pincodeDays: DayAreas[];
  isAdd: boolean;
  addPincodeDay: (data: DayAreas) => Promise<boolean>;
  updatePincodeDay: (data: DayAreas) => Promise<boolean>;
}

export const AddEWastePincodeDay: FC<AddEWastePincodeProps> = (props) => {
  const {
    open,
    onClose,
    cpServiceAreaIds,
    isAdd,
    pincodeDays,
    addPincodeDay,
    updatePincodeDay
  } = props;

  const [isAvailable, setIsAvailable] = useState<string>('Loading');
  const [serviceAreaList, setServiceAreaList] = useState<Pincodes[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [districts, setDistricts] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [codes, setCodes] = useState<string[]>([]);

  // Safe initialization with proper error handling
  const [selectedDay, setSelectedDay] = useState<number>(() => {
    try {
      if (isAdd || !pincodeDays || pincodeDays.length === 0) return null;
      return pincodeDays[0]?.day || null;
    } catch (error) {
      console.error('Error initializing selectedDay:', error);
      return null;
    }
  });

  const [selectedcodes, setSelectedcodes] = useState<string[]>(() => {
    try {
      if (isAdd || !pincodeDays || pincodeDays.length === 0) return [];
      const areas = pincodeDays[0]?.areas || [];
      const processed = areas.map((area) => {
        if (typeof area === 'string' && area.includes('#')) {
          return area.split('#')[0];
        }
        return area.toString();
      });
      const unique = [...new Set(processed)];
      console.log(
        'Initial selectedcodes - areas:',
        areas,
        'processed:',
        processed,
        'unique:',
        unique
      );
      return unique;
    } catch (error) {
      console.error('Error initializing selectedcodes:', error);
      return [];
    }
  });

  // For setting height of filtered data
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 300,
      },
    },
  };

  // Safe dialog close with proper cleanup
  const closeDialog = (e, reason) => {
    try {
      if (reason !== 'backdropClick') {
        setSelectedState('');
        setSelectedDistricts([]);
        setSelectedCities([]);

        // Reset to original values in edit mode
        if (!isAdd && pincodeDays && pincodeDays.length > 0) {
          const areas = pincodeDays[0]?.areas || [];
          const processed = areas.map((area) => {
            if (typeof area === 'string' && area.includes('#')) {
              return area.split('#')[0];
            }
            return area.toString();
          });
          setSelectedcodes([...new Set(processed)]);
        } else {
          setSelectedcodes([]);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error in closeDialog:', error);
      onClose();
    }
  };

  const handleAddOrUpdatePincodeDay = async () => {
    try {
      const areas = selectedcodes.map((c) => c.toString());
      if (isAdd) {
        const addResult = await addPincodeDay({ day: selectedDay, areas });
        if (addResult) {
          closeDialog('', '');
        }
      } else {
        const updateResult = await updatePincodeDay({ day: selectedDay, areas });
        if (updateResult) {
          closeDialog('', '');
        }
      }
    } catch (error) {
      console.error('Error in handleAddOrUpdatePincodeDay:', error);
    }
  };

  // Handle selected state - clear dependent dropdowns
  const handleStateChange = async (event): Promise<void> => {
    try {
      setSelectedState(event.target.value);
      setSelectedDistricts([]); // Clear districts when state changes
      setSelectedCities([]); // Clear cities when state changes
    } catch (error) {
      console.error('Error in handleStateChange:', error);
    }
  };

  // Handle selected district - clear dependent dropdowns
  const handleDistChange = async (event): Promise<void> => {
    try {
      setSelectedDistricts(event.target.value);
      setSelectedCities([]); // Clear cities when districts change
    } catch (error) {
      console.error('Error in handleDistChange:', error);
    }
  };

  // Handle selected city - no dependent dropdowns to clear
  const handleCityChange = async (event): Promise<void> => {
    try {
      setSelectedCities(event.target.value);
    } catch (error) {
      console.error('Error in handleCityChange:', error);
    }
  }; // Handle selected pincode with proper deduplication
  const handlePincodesChange = (event): void => {
    try {
      const selectedValues = event.target.value as string[];
      const uniqueValues = [...new Set(selectedValues)];
      console.log(
        'handlePincodesChange - selectedValues:',
        selectedValues,
        'uniqueValues:',
        uniqueValues
      );
      setSelectedcodes(uniqueValues);
    } catch (error) {
      console.error('Error in handlePincodesChange:', error);
    }
  };

  const handleDayChange = (event): void => {
    try {
      setSelectedDay(event.target.value);
    } catch (error) {
      console.error('Error in handleDayChange:', error);
    }
  };

  // Fetch pincodes based on service areas
  const getPincodesByServiceAreaIds = async (array: string[]) => {
    try {
      console.log('DEBUG: cpServiceAreaIds received:', array);
      if (!array || array.length === 0) {
        setIsAvailable('Fail');
        return;
      }

      const pincodes = new Set<number>();
      array.forEach((item) => {
        if (typeof item === 'string') {
          if (item.includes('#')) {
            const [pincode] = item.split('#');
            pincodes.add(Number(pincode));
          } else {
            // Handle plain pincode numbers
            pincodes.add(Number(item));
          }
        }
      });
      const codeArray = Array.from(pincodes);
      console.log('DEBUG: Extracted pincodes:', codeArray);
      if (codeArray.length === 0) {
        setIsAvailable('Fail');
        return;
      }

      const paginationObject: PaginationAndFilter = {
        filters: [
          {
            property: 'code',
            operator: 'in',
            value: codeArray,
          },
        ],
        pageSize: 9999,
        pageNumber: 1,
        sort: -1,
        sortBy: 'code',
      };

      const paginationResult = await pincodeApi.getPincodesByPaginationAndFilters(paginationObject);
      const pincodesRecords: Pincodes[] = paginationResult?.responseData?.records || [];

      setServiceAreaList(pincodesRecords);
      setStates(getUniqueIds(pincodesRecords, 'state'));
      setDistricts(getUniqueIds(pincodesRecords, 'district'));
      setCities(getUniqueIds(pincodesRecords, 'city'));

      // Process codes to handle # format
      const processedCodes = pincodesRecords.map((record) => {
        if (record.code && typeof record.code === 'string') {
          return record.code.includes('#') ? record.code.split('#')[0] : record.code;
        }
        return record.code?.toString() || '';
      });
      const uniqueCodes = [...new Set(processedCodes)].filter((code) => code !== '');
      setCodes(uniqueCodes);

      setIsAvailable('Success');

      // Preserve selected codes in edit mode after data is loaded
      if (!isAdd && pincodeDays && pincodeDays.length > 0) {
        const existingCodes = pincodeDays[0]?.areas || [];
        if (existingCodes.length > 0) {
          const pincodeNumbers = existingCodes.map((area) => {
            if (typeof area === 'string' && area.includes('#')) {
              return area.split('#')[0];
            }
            return area.toString();
          });
          const uniquePincodes = [...new Set(pincodeNumbers)];
          setSelectedcodes(uniquePincodes);
        }
      }
    } catch (error) {
      console.error('Error in getPincodesByServiceAreaIds:', error);
      setIsAvailable('Fail');
    }
  };

  // Load pincodes when dialog opens and service areas are available
  useEffect(() => {
    if (open && cpServiceAreaIds && cpServiceAreaIds.length > 0) {
      getPincodesByServiceAreaIds(cpServiceAreaIds).catch(console.error);
    } else if (open && (!cpServiceAreaIds || cpServiceAreaIds.length === 0)) {
      setIsAvailable('Fail');
    }
  }, [open, cpServiceAreaIds]);

  // Initialize selected codes when dialog opens in edit mode
  useEffect(() => {
    if (!isAdd && pincodeDays && pincodeDays.length > 0) {
      try {
        const pincodeNumbers = (pincodeDays[0]?.areas || []).map((area) => {
          if (typeof area === 'string' && area.includes('#')) {
            return area.split('#')[0];
          }
          return area.toString();
        });
        const uniquePincodes = [...new Set(pincodeNumbers)];
        setSelectedcodes(uniquePincodes);
      } catch (error) {
        console.error('Error in useEffect for selectedcodes:', error);
        setSelectedcodes([]);
      }
    }
  }, [isAdd, pincodeDays]);

  // Preserve selected codes when dropdown options change
  useEffect(() => {
    if (selectedcodes.length > 0) {
      console.log('Preserving selected codes when dropdown changes:', selectedcodes);
      // Force re-render to maintain selected state
      const currentSelected = [...selectedcodes];
      setTimeout(() => {
        setSelectedcodes(currentSelected);
      }, 0);
    }
  }, [codes]);

  const clearFilter = () => {
    try {
      setSelectedState('');
      setSelectedDistricts([]);
      setSelectedCities([]);

      if (!isAdd && pincodeDays && pincodeDays.length > 0) {
        const areas = pincodeDays[0]?.areas || [];
        const processed = areas.map((area) => {
          if (typeof area === 'string' && area.includes('#')) {
            return area.split('#')[0];
          }
          return area.toString();
        });
        setSelectedcodes([...new Set(processed)]);
      } else {
        setSelectedcodes([]);
      }
    } catch (error) {
      console.error('Error in clearFilter:', error);
    }
  };

  // Cascading dropdown logic - filter options based on selections
  useEffect(() => {
    try {
      let filtered = serviceAreaList;

      // Filter by state
      if (selectedState !== '') {
        filtered = filtered.filter((item) => item.state === selectedState);
      }

      // Get available districts based on current filters
      const availableDistricts = Array.from(new Set(filtered.map((item) => item.district)))
        .filter(Boolean);
      setDistricts(availableDistricts);

      // Filter by districts
      if (selectedDistricts.length > 0) {
        filtered = filtered.filter((item) => selectedDistricts.includes(item.district));
      }

      // Get available cities based on current filters
      const availableCities = Array.from(new Set(filtered.map((item) => item.city)))
        .filter(Boolean);
      setCities(availableCities);

      // Filter by cities
      if (selectedCities.length > 0) {
        filtered = filtered.filter((item) => selectedCities.includes(item.city));
      }

      // Get available pincodes based on current filters
      const availableCodes = Array.from(new Set(filtered.map((i) => {
        if (i.code && typeof i.code === 'string') {
          return i.code.includes('#') ? i.code.split('#')[0] : i.code;
        }
        return i.code?.toString() || '';
      }))).filter((code) => code !== '');
      setCodes(availableCodes);

      console.log('Cascading filters - State:', selectedState, 'Districts:', availableDistricts, 'Cities:', availableCities, 'Codes:', availableCodes);
    } catch (error) {
      console.error('Error in cascading filter useEffect:', error);
    }
  }, [serviceAreaList, selectedState, selectedDistricts, selectedCities]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle sx={{ justifyContent: 'flex-end', pb: 1 }}>
        {`${isAdd ? 'Add' : 'Edit'} Pincode Day Mapping`}
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {isAvailable === 'Loading' && (
        <>
          <Typography
            sx={{
              ml: 4,
              mb: 4,
            }}
            variant="body2"
          >
            Loading...
          </Typography>
        </>
      )}
      {isAvailable === 'Fail' && (
        <Typography
          sx={{
            ml: 4,
            mb: 4,
          }}
          color="red"
          variant="body2"
        >
          Please add at least 1 Service Area
        </Typography>
      )}
      {isAvailable === 'Success' && (
        <>
          <DialogContent>
            <Grid
              container
              mb={1}
              pt={1}
            >
              <Grid
                item
                xs={3}
                md={2}
                sm={3}
                ml={0}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Select Day
                </Typography>
              </Grid>
              <Grid
                item
                md={10}
                sm={9}
                xs={9}
              >
                <InputField
                  fullWidth
                  placeholder="Select Day"
                  name="day"
                  onChange={(event) => {
                    handleDayChange(event);
                  }}
                  disabled={!isAdd}
                  select
                  required
                  value={selectedDay}
                  sx={{
                    maxWidth: '220px',
                  }}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <MenuItem
                      key={day}
                      value={day}
                    >
                      {day}
                    </MenuItem>
                  ))}
                </InputField>
              </Grid>
            </Grid>
            <Grid
              container
              spacing={1}
            >
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  State
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="state-select"
                    value={selectedState}
                    onChange={(event) => {
                      handleStateChange(event);
                    }}
                    renderValue={(t) => t}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {states?.map((item) => (
                      <MenuItem
                        key={item}
                        value={item}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  District
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="district-multi-select"
                    multiple
                    value={selectedDistricts}
                    onChange={(event) => {
                      handleDistChange(event);
                    }}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {districts.map((district) => (
                      <MenuItem
                        key={district}
                        value={district}
                      >
                        <ListItemIcon>
                          <Checkbox checked={selectedDistricts.indexOf(district) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={district} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={3}
                sm={3}
                xs={3}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Cities
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="city-multi-select"
                    multiple
                    value={selectedCities}
                    onChange={(event) => {
                      handleCityChange(event);
                    }}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                    }}
                    MenuProps={MenuProps}
                  >
                    {cities.map((city) => (
                      <MenuItem
                        key={city}
                        value={city}
                      >
                        <ListItemIcon>
                          <Checkbox checked={selectedCities.indexOf(city) > -1} />
                        </ListItemIcon>
                        <ListItemText primary={city} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={2}
                sm={2}
                xs={2}
              >
                <Typography
                  sx={{ textAlign: 'left' }}
                  variant="subtitle2"
                >
                  Pincode
                </Typography>
                <FormControl fullWidth>
                  <Select
                    labelId="pincode-multi-select"
                    multiple
                    value={selectedcodes}
                    onChange={(event) => handlePincodesChange(event)}
                    renderValue={(t) => t.join(', ')}
                    style={{
                      fontSize: '12px',
                      height: '35px',
                      width: '100%',
                    }}
                    MenuProps={MenuProps}
                  >
                    {codes?.map((option) => {
                      const isChecked = selectedcodes.includes(option);
                      console.log(
                        'Checkbox check - option:',
                        option,
                        'selectedcodes:',
                        selectedcodes,
                        'isChecked:',
                        isChecked
                      );
                      return (
                        <MenuItem
                          key={option}
                          value={option}
                        >
                          <ListItemIcon>
                            <Checkbox checked={isChecked} />
                          </ListItemIcon>
                          <ListItemText primary={option} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                md={1}
                sm={1}
                xs={1}
              >
                <Button
                  sx={{
                    mt: 3,
                  }}
                  onClick={() => {
                    clearFilter();
                  }}
                  variant="contained"
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
            <Box
              sx={{
                minHeight: 250,
              }}
            >
              {selectedcodes.length > 0 && (
                <Box>
                  <Typography
                    color="textPrimary"
                    sx={{ pt: 2, mb: 1 }}
                    variant="h6"
                  >
                    Selected Pincodes:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedcodes.map((code) => (
                      <Chip
                        key={code}
                        label={code}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              alignItems: 'right',
            }}
          >
            <Button
              color="primary"
              onClick={(e) => {
                closeDialog(e, '');
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              disabled={
                selectedcodes.length < 1
                || selectedDay === null
                || (!isAdd
                  && pincodeDays
                  && pincodeDays.length > 0
                  && selectedcodes.every((val) => pincodeDays[0].areas?.includes(val))
                  && selectedcodes?.length === pincodeDays[0].areas?.length)
              }
              onClick={() => {
                handleAddOrUpdatePincodeDay();
              }}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

AddEWastePincodeDay.defaultProps = {
  open: false,
};

AddEWastePincodeDay.propTypes = {
  pincodeDays: PropTypes.array,
  open: PropTypes.bool,
  isAdd: PropTypes.bool,
  onClose: PropTypes.func,
  addPincodeDay: PropTypes.func,
  updatePincodeDay: PropTypes.func,
  cpServiceAreaIds: PropTypes.array,
};
